import { Injectable }                                                                             from '@angular/core';
import {
	ArchivingContractTypeEnum,
	ContractStatusEnum,
	ContractTypeGroupEnum
}                                                                                                 from '@app/modules/contract/models/contract.model';
import { LanguageEnum }                                                                           from '@models/language';
import { ContractTypeGroupEnumDTO }                                                               from '@openApiGenerated/admin-api';
import {
	ContractTypeEnumDTO as ArchivingContractTypeEnumDTO,
	EmailNotificationDetailDTOLanguageEnum as ArchivingEmailNotificationDetailDTOLanguageEnum
}                                                                                                 from '@openApiGenerated/archiving-usage-api';
import { EmailNotificationDetailDTOLanguageEnum as ElsterEmailNotificationDetailDTOLanguageEnum } from '@openApiGenerated/eric-wrapper-api';
import {
	ContractStatusEnumDTO,
	EmailNotificationDetailDTOLanguageEnum as FiskalEmailNotificationDetailDTOLanguageEnum
}                                                                                                 from '@openApiGenerated/management-api';
import { LoggerService }                                                                          from '@shared/services/logger.service';
import { StringUtils }                                                                            from '@utils/helpers';


@Injectable( { providedIn: 'root' } )
export class ContractsMapperService {

	constructor(
		private _logger: LoggerService
	) {}

	get contracts() {
		return {
			archivingEmailDetailLanguageTmoToDto:
				this.mapArchivingEmailNotificationDetailLanguageTmoToDto.bind( this ) as typeof this.mapArchivingEmailNotificationDetailLanguageTmoToDto,
			archivingTypeTmoToDto: this.mapArchivingContractTypeTmoToDto.bind( this ) as typeof this.mapArchivingContractTypeTmoToDto,
			archivingTypeListTmoToDto: this.mapArchivingContractTypeListTmoToDto.bind( this ) as typeof this.mapArchivingContractTypeListTmoToDto,
			elsterEmailDetailLanguageTmoToDto:
				this.mapElsterEmailNotificationDetailLanguageTmoToDto.bind( this ) as typeof this.mapElsterEmailNotificationDetailLanguageTmoToDto,
			fiskalEmailDetailLanguageTmoToDto:
				this.mapFiskalEmailNotificationDetailLanguageTmoToDto.bind( this ) as typeof this.mapFiskalEmailNotificationDetailLanguageTmoToDto,
			fiskalTypeGroupDtoToTmo: this.mapFiskalContractTypeGroupDtoToTmo.bind( this ) as typeof this.mapFiskalContractTypeGroupDtoToTmo,
			fiskalTypeGroupTmoToDto: this.mapFiskalContractTypeGroupTmoToDto.bind( this ) as typeof this.mapFiskalContractTypeGroupTmoToDto,
			fiskalTypeGroupListDtoToTmo: this.mapFiskalContractTypeGroupListDtoToTmo.bind( this ) as typeof this.mapFiskalContractTypeGroupListDtoToTmo,
			fiskalTypeGroupListTmoToDto: this.mapFiskalContractTypeGroupListTmoToDto.bind( this ) as typeof this.mapFiskalContractTypeGroupListTmoToDto,
			statusTmoToDto: this.mapContractStatusTmoToDto.bind( this ) as typeof this.mapContractStatusTmoToDto,
			statusListTmoToDto: this.mapContractStatusListTmoToDto.bind( this ) as typeof this.mapContractStatusListTmoToDto,
		};
	}

	// archiving contract type
	private mapArchivingContractTypeTmoToDto( archivingContractType: ArchivingContractTypeEnum ): ArchivingContractTypeEnumDTO {
		switch ( archivingContractType ) {
			case ArchivingContractTypeEnum.LIGHT:
				return ArchivingContractTypeEnumDTO.LIGHT;

			case ArchivingContractTypeEnum.STANDARD:
				return ArchivingContractTypeEnumDTO.STANDARD;

			case ArchivingContractTypeEnum.UNLIMITED:
				return ArchivingContractTypeEnumDTO.UNLIMITED;

			default:
				this._logger.warn( `ContractsMapperService:mapArchivingContractTypeTmoToDto -> no existing map case ${ archivingContractType }` );
		}
	}

	private mapArchivingContractTypeListTmoToDto( archivingContractTypeList: ArchivingContractTypeEnum[] ): ArchivingContractTypeEnumDTO[] {
		const dtoContractTypeList: ArchivingContractTypeEnumDTO[] = archivingContractTypeList?.map(
			( archivingContractType: ArchivingContractTypeEnum ) => this.mapArchivingContractTypeTmoToDto( archivingContractType )
		);

		return dtoContractTypeList;
	}


	// email notification detail language
	private mapArchivingEmailNotificationDetailLanguageTmoToDto( language: LanguageEnum ): ArchivingEmailNotificationDetailDTOLanguageEnum {
		switch ( language ) {
			case LanguageEnum.ENGLISH:
				return ArchivingEmailNotificationDetailDTOLanguageEnum.EN;

			case LanguageEnum.GERMAN:
				return ArchivingEmailNotificationDetailDTOLanguageEnum.DE;

			default:
				this._logger.warn( `ContractsMapperService:mapArchivingEmailNotificationDetailLanguageTmoToDto -> no existing map case ${ language }` );
		}
	}

	private mapElsterEmailNotificationDetailLanguageTmoToDto( language: LanguageEnum ): ElsterEmailNotificationDetailDTOLanguageEnum {
		switch ( language ) {
			case LanguageEnum.ENGLISH:
				return ElsterEmailNotificationDetailDTOLanguageEnum.EN;

			case LanguageEnum.GERMAN:
				return ElsterEmailNotificationDetailDTOLanguageEnum.DE;

			default:
				this._logger.warn( `ContractsMapperService:mapElsterEmailNotificationDetailLanguageTmoToDto -> no existing map case ${ language }` );
		}
	}

	private mapFiskalEmailNotificationDetailLanguageTmoToDto( language: LanguageEnum ): FiskalEmailNotificationDetailDTOLanguageEnum {
		switch ( language ) {
			case LanguageEnum.ENGLISH:
				return FiskalEmailNotificationDetailDTOLanguageEnum.EN;

			case LanguageEnum.GERMAN:
				return FiskalEmailNotificationDetailDTOLanguageEnum.DE;

			default:
				this._logger.warn( `ContractsMapperService:mapFiskalEmailNotificationDetailLanguageTmoToDto -> no existing map case ${ language }` );
		}
	}

	// fiskal contract type group
	private mapFiskalContractTypeGroupDtoToTmo( dtoFiskalContractTypeGroup: ContractTypeGroupEnumDTO ): ContractTypeGroupEnum {
		switch ( dtoFiskalContractTypeGroup ) {
			case ContractTypeGroupEnumDTO.A:
				return ContractTypeGroupEnum.A;

			case ContractTypeGroupEnumDTO.G:
				return ContractTypeGroupEnum.G;

			case ContractTypeGroupEnumDTO.S:
				return ContractTypeGroupEnum.S;

			case ContractTypeGroupEnumDTO.V:
				return ContractTypeGroupEnum.V;

			default:
				this._logger.warn(
					`ContractsMapperService:mapFiskalContractTypeGroupDtoToTmo -> no existing map case ${ dtoFiskalContractTypeGroup }`
				);
				return dtoFiskalContractTypeGroup
							 ? StringUtils.toCamelCase( dtoFiskalContractTypeGroup ) as ContractTypeGroupEnum
							 : null;
		}
	}

	private mapFiskalContractTypeGroupTmoToDto( fiskalContractTypeGroup: ContractTypeGroupEnum ): ContractTypeGroupEnumDTO {
		switch ( fiskalContractTypeGroup ) {
			case ContractTypeGroupEnum.A:
				return ContractTypeGroupEnumDTO.A;

			case ContractTypeGroupEnum.G:
				return ContractTypeGroupEnumDTO.G;

			case ContractTypeGroupEnum.S:
				return ContractTypeGroupEnumDTO.S;

			case ContractTypeGroupEnum.V:
				return ContractTypeGroupEnumDTO.V;

			default:
				this._logger.warn( `ContractsMapperService:mapFiskalContractTypeGroupTmoToDto -> no existing map case ${ fiskalContractTypeGroup }` );
		}
	}

	private mapFiskalContractTypeGroupListDtoToTmo( dtoFiskalContractTypeGroupList: ContractTypeGroupEnumDTO[] ): ContractTypeGroupEnum[] {
		const tmoContractTypeGroupList: ContractTypeGroupEnum[] = dtoFiskalContractTypeGroupList?.map(
			( dtoFiskalContractTypeGroup: ContractTypeGroupEnumDTO ) => this.mapFiskalContractTypeGroupDtoToTmo( dtoFiskalContractTypeGroup )
		);

		return tmoContractTypeGroupList;
	}

	private mapFiskalContractTypeGroupListTmoToDto( fiskalContractTypeGroupList: ContractTypeGroupEnum[] ): ContractTypeGroupEnumDTO[] {
		const dtoContractTypeGroupList: ContractTypeGroupEnumDTO[] = fiskalContractTypeGroupList?.map(
			( fiskalContractTypeGroup: ContractTypeGroupEnum ) => this.mapFiskalContractTypeGroupTmoToDto( fiskalContractTypeGroup )
		);

		return dtoContractTypeGroupList;
	}

	// status
	private mapContractStatusTmoToDto( contractStatus: ContractStatusEnum ): ContractStatusEnumDTO {
		switch ( contractStatus ) {
			case ContractStatusEnum.ACTIVE:
				return ContractStatusEnumDTO.ACTIVE;

			case ContractStatusEnum.DISABLED:
				return ContractStatusEnumDTO.DISABLED;

			default:
				this._logger.warn( `ContractsMapperService:mapContractStatusTmoToDto -> no existing map case ${ contractStatus }` );
		}
	}

	private mapContractStatusListTmoToDto( contractStatusList: ContractStatusEnum[] ): ContractStatusEnumDTO[] {
		const dtoContractStatusList: ContractStatusEnumDTO[] = contractStatusList?.map(
			( contractStatus: ContractStatusEnum ) => this.mapContractStatusTmoToDto( contractStatus )
		);

		return dtoContractStatusList;
	}
}
